/* <ss-delivery-check>: pincode -> estimated delivery date under the PDP buy buttons (snippets/ss-delivery-check.liquid).
 * - Asks Shuddh Swad's courier TAT API (data-api + pincode). It takes about 3 s, so each answer is kept for the rest
 *   of the day (IST) per pincode, and the pincode the shopper typed is remembered for the next visit.
 * - Fills the pincode by itself, first match wins: the pincode last checked on this device; the logged-in customer's
 *   default address (data-account-pin); an approximate pincode from the visitor's network (data-ip-guess: GeoJS for
 *   the area, OpenStreetMap Nominatim for its pincode, kept 7 days and shown as "near {city}").
 * - "Use my location" asks the browser for the location only when tapped, then looks up the exact pincode.
 * - Starts after the page has loaded, so it never competes with the product photos for bandwidth.
 * - Every answer is checked against the latest request (this.run), so a slow automatic lookup never overwrites a
 *   pincode the shopper typed. */
if (!customElements.get('ss-delivery-check')) {
  const KEY_PIN = 'ss_dc_pin';
  const KEY_RESULTS = 'ss_dc_results';
  const KEY_GUESS = 'ss_dc_guess';
  const GUESS_MS = 7 * 24 * 60 * 60 * 1000;
  const GEO_IP = 'https://get.geojs.io/v1/ip/geo.json';
  const REVERSE = 'https://nominatim.openstreetmap.org/reverse?format=jsonv2&addressdetails=1&accept-language=en';
  const MONTHS = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];
  const TICK =
    '<svg class="ss-dc__tick" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.25" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M5 12.5l4.5 4.5L19 7.5"/></svg>';

  /* localStorage can be missing or full (private mode): the check still works, it just doesn't remember. */
  const store = {
    get(key) {
      try {
        return JSON.parse(localStorage.getItem(key));
      } catch (e) {
        return null;
      }
    },
    set(key, value) {
      try {
        localStorage.setItem(key, JSON.stringify(value));
      } catch (e) {
        /* nothing to keep */
      }
    },
  };

  const validPin = (pin) => /^[1-9][0-9]{5}$/.test(pin);
  const today = () => new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });

  const getJson = async (url, ms = 12000) => {
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), ms);
    try {
      const res = await fetch(url, { signal: ctrl.signal });
      // A server error is not an answer: its {"success":false} body used to read as "we don't deliver to <pin>".
      if (res.status >= 500) throw new Error(`HTTP ${res.status}`);
      return await res.json();
    } finally {
      clearTimeout(timer);
    }
  };

  /* "29 Oct 2026" -> "Thu, 29 Oct". Split by hand: Safari can't Date.parse that format. */
  const niceDate = (text) => {
    const m = /^(\d{1,2})\s+([a-z]{3})[a-z]*\s+(\d{4})$/i.exec(String(text || '').trim());
    const month = m ? MONTHS.indexOf(m[2].toLowerCase()) : -1;
    if (month < 0) return String(text || '');
    const date = new Date(Number(m[3]), month, Number(m[1]));
    return date.toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' });
  };

  customElements.define(
    'ss-delivery-check',
    class SsDeliveryCheck extends HTMLElement {
      connectedCallback() {
        this.form = this.querySelector('form');
        this.input = this.querySelector('input');
        this.result = this.querySelector('[data-dc-result]');
        this.locateButton = this.querySelector('[data-dc-locate]');
        if (!this.form || !this.input || !this.result || !this.dataset.api) return;
        this.run = 0;

        this.form.addEventListener('submit', (event) => {
          event.preventDefault();
          this.check(this.input.value, 'typed');
        });
        this.input.addEventListener('input', () => {
          const digits = this.input.value.replace(/\D/g, '').slice(0, 6);
          if (digits !== this.input.value) this.input.value = digits;
        });
        if (this.locateButton) {
          if ('geolocation' in navigator) this.locateButton.addEventListener('click', () => this.locate());
          else this.locateButton.hidden = true;
        }

        const start = () => this.autoFill();
        if (document.readyState === 'complete') start();
        else window.addEventListener('load', start, { once: true });
      }

      /* ---------- where the pincode comes from ---------- */

      async autoFill() {
        if (this.input.value) return;
        const saved = String(store.get(KEY_PIN) || '');
        if (validPin(saved)) return this.check(saved, 'saved');
        const account = (this.dataset.accountPin || '').replace(/\s/g, '');
        if (validPin(account)) return this.check(account, 'account');
        if (this.dataset.ipGuess !== 'true') return;

        const run = ++this.run;
        const guess = await this.networkGuess();
        if (guess && run === this.run && !this.input.value) this.check(guess.pin, 'network', guess.city);
      }

      async networkGuess() {
        const cached = store.get(KEY_GUESS);
        if (cached && validPin(cached.pin) && Date.now() - cached.at < GUESS_MS) return cached;
        try {
          const ip = await getJson(GEO_IP);
          if (!ip || ip.country_code !== 'IN' || !ip.latitude || !ip.longitude) return null;
          const place = await this.pincodeAt(ip.latitude, ip.longitude, 14);
          if (!place) return null;
          const guess = { pin: place.pin, city: ip.city || place.city, at: Date.now() };
          store.set(KEY_GUESS, guess);
          return guess;
        } catch (e) {
          return null;
        }
      }

      async pincodeAt(lat, lon, zoom) {
        const data = await getJson(
          `${REVERSE}&zoom=${zoom}&lat=${encodeURIComponent(lat)}&lon=${encodeURIComponent(lon)}`
        );
        const address = (data && data.address) || {};
        const pin = String(address.postcode || '').replace(/\s/g, '');
        if (!validPin(pin)) return null;
        return { pin, city: address.city || address.town || address.village || address.county || '' };
      }

      locate() {
        const run = ++this.run;
        this.show('loading', 'Finding your location…');
        const fail = (text) => {
          if (run !== this.run) return;
          this.show('error', text);
          this.input.focus();
        };
        navigator.geolocation.getCurrentPosition(
          async (position) => {
            try {
              const place = await this.pincodeAt(position.coords.latitude, position.coords.longitude, 18);
              if (run !== this.run) return;
              if (place) this.check(place.pin, 'location', place.city);
              else fail("We couldn't find a pincode for your location. Please type it in.");
            } catch (e) {
              fail("We couldn't find your location. Please type your pincode.");
            }
          },
          (error) =>
            fail(
              error.code === 1
                ? 'Location access is off for this site. Please type your pincode.'
                : "We couldn't find your location. Please type your pincode."
            ),
          { timeout: 10000, maximumAge: 600000 }
        );
      }

      /* ---------- the check ---------- */

      async check(value, source, city) {
        const pin = String(value || '').replace(/\D/g, '');
        this.input.value = pin;
        const run = ++this.run;
        if (!validPin(pin)) {
          this.show('error', 'Enter a valid 6-digit pincode.');
          this.input.focus();
          return;
        }

        const day = today();
        const results = store.get(KEY_RESULTS) || {};
        let data = results[pin] && results[pin].day === day ? results[pin].data : null;
        if (!data) {
          this.show('loading', `Checking delivery to ${pin}…`);
          try {
            data = await getJson(this.dataset.api + pin);
          } catch (e) {
            data = null;
          }
          if (run !== this.run) return;
          if (!data) {
            this.show('error', "Couldn't check delivery right now. Please try again.");
            return;
          }
          if (data.success && data.delivery) {
            /* Today's answers only, at most 8 pincodes. */
            const kept = Object.entries(results).filter(([, entry]) => entry && entry.day === day).slice(-7);
            store.set(KEY_RESULTS, Object.assign(Object.fromEntries(kept), { [pin]: { day, data } }));
          }
        }

        if (source === 'typed' || source === 'location') store.set(KEY_PIN, pin);
        this.render(data, pin, source, city);
      }

      render(data, pin, source, city) {
        if (!data.success || !data.delivery) {
          if (source === 'network') {
            /* A guessed pincode that doesn't work: say nothing, the shopper can type theirs. */
            this.input.value = '';
            this.show('', '');
            return;
          }
          this.show(
            'error',
            /invalid/i.test(data.error || '')
              ? `${pin} doesn't look like a valid pincode. Please check it.`
              : `Sorry, we don't deliver to ${pin} yet.`
          );
          return;
        }

        const date = niceDate(data.delivery.estimated_delivery_date) || data.delivery.message || '';
        let note = `to ${pin}`;
        if (source === 'network') note += `${city ? `, near ${city}` : ''} (from your network). Not yours? Type your pincode.`;
        else if (source === 'location') note += `${city ? `, ${city}` : ''} (your location)`;
        else if (source === 'account') note += ' (your saved address)';

        this.result.className = 'ss-dc__result ss-dc__result--ok';
        this.result.innerHTML = `<p class="ss-dc__eta">${TICK}<span>Delivery by <strong></strong></span></p><p class="ss-dc__note"></p>`;
        this.result.querySelector('strong').textContent = date;
        this.result.querySelector('.ss-dc__note').textContent = note;
      }

      show(state, text) {
        this.result.className = `ss-dc__result${state ? ` ss-dc__result--${state}` : ''}`;
        this.result.textContent = '';
        if (!text) return;
        const line = document.createElement('p');
        line.className = 'ss-dc__msg';
        if (state === 'loading') {
          const spinner = document.createElement('span');
          spinner.className = 'ss-dc__spinner';
          spinner.setAttribute('aria-hidden', 'true');
          line.append(spinner);
        }
        line.append(text);
        this.result.append(line);
      }
    }
  );
}